import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Tooltip,
  MenuItem,
  FormControlLabel,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import SelectUi from '@mui/material/Select';
import { Controller, UseFormReturn } from 'react-hook-form';
import { projectSimulatorFieldRules, selectStyle, Unit } from '../utils';
import { InputProjectSimulator } from '../ProjectSimulator';

type GeneralInformationProps = {
  general: GeneralInformationData;
  ddv: number;
  availableUnits: Unit[];
  refUnit: Unit;
  form: UseFormReturn;
};

export type GeneralInformationData = {
  projectType: 'NEW';
};

function GeneralInformation({
  general,
  ddv,
  availableUnits,
  refUnit,
  form,
}: GeneralInformationProps) {
  const { t } = useTranslation();

  return (
    <div className="form-block">
      <h2 className="form-title">
        {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.GENERAL.INFORMATION.TITLE', {
          defaultValue: 'Informations générales',
        })}
      </h2>

      <hr className="line-title-separation" />

      <div
        style={{
          display: 'flex',
          paddingLeft: '24px',
          paddingRight: '96px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginRight: '24px',
            rowGap: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <label className="form-label" htmlFor="lifespan">
              {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.LIFESPAN', {
                defaultValue: 'Durée de vie',
              })}
              <Tooltip
                title={t(
                  'APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.LIFESPAN_ERROR',
                  {
                    defaultValue:
                      'La durée de vie ne peut être inférieure à 1 an ni supérieure à 50 ans',
                  }
                )}
              >
                <InfoIcon
                  fontSize="small"
                  style={{
                    marginLeft: '4px',
                  }}
                  htmlColor="#A3AED0"
                />
              </Tooltip>
            </label>
            <InputProjectSimulator
              form={form}
              style={{ width: '160px' }}
              name="lifespan"
              rules={projectSimulatorFieldRules.lifeSpan}
              defaultValue={ddv}
              hideValueIfFormNotPatched={true}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <label className="form-label" htmlFor="unit">
              {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.UNIT', {
                defaultValue: "Choix de l'unité de référence",
              })}
              <Tooltip
                title={t(
                  'APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.UNIT_TOOLTIP',
                  {
                    defaultValue:
                      'Tous les calculs d’impact de votre projet seront exprimés selon l’unité de référence choisie : /m² de la surface de référence du projet ou bien en total kg éq. CO2.',
                  }
                )}
              >
                <InfoIcon
                  fontSize="small"
                  style={{
                    marginLeft: '4px',
                  }}
                  htmlColor="#A3AED0"
                />
              </Tooltip>
            </label>

            <Controller
              control={form.control}
              name="refUnit"
              defaultValue={refUnit}
              render={({ field }) => (
                <SelectUi
                  {...field}
                  sx={selectStyle}
                  value={field.value ?? refUnit}
                  style={{ width: '160px' }}
                >
                  {availableUnits.map((unit) => (
                    <MenuItem value={unit}>{unit}</MenuItem>
                  ))}
                </SelectUi>
              )}
            />
          </div>
        </div>

        <Controller
          name="projectType"
          control={form.control}
          defaultValue={general.projectType}
          render={({ field }) => (
            <FormControl style={{ paddingLeft: '24px' }}>
              <FormLabel>
                <p className="form-radio-control-label">
                  {t(
                    'APP.PROJECT.SIMULATOR.QUICK.SIMULATION.RADIOGROUP.PROJECT_TYPE',
                    {
                      defaultValue: 'Type de projet',
                    }
                  )}
                </p>
              </FormLabel>
              <RadioGroup {...field} value={general.projectType}>
                <FormControlLabel
                  control={<Radio color="primary" value={general.projectType} />}
                  label={t(
                    'APP.PROJECT.SIMULATOR.QUICK.SIMULATION.RADIOGROUP.NEW.OPTION',
                    {
                      defaultValue: 'Neuf',
                    }
                  )}
                />
                <Tooltip
                  title={t(
                    'APP.PROJECT.SIMULATOR.QUICK.SIMULATION.RADIOGROUP.NOT.AVAILABLE.TOOLTIP',
                    {
                      defaultValue: 'Bientôt disponible',
                    }
                  )}
                >
                  <FormControlLabel
                    value="renovation"
                    control={<Radio color="primary" disabled />}
                    label={t(
                      'APP.PROJECT.SIMULATOR.QUICK.SIMULATION.RADIOGROUP.RENOVATION.OPTION',
                      {
                        defaultValue: 'Rénovation',
                      }
                    )}
                    style={{ cursor: 'not-allowed' }}
                  />
                </Tooltip>
              </RadioGroup>
            </FormControl>
          )}
        />
      </div>
    </div>
  );
}

export default GeneralInformation;
